// StaticTable.jsx
import React, { useMemo } from "react";

export default function StaticTable({
  columns = [],
  data = [],
  pinRowPredicate,
  striped = true,
  maxRows, // optional: potong tampilan ke N baris
  showOverflowHint = true, // tampilkan "… +N more" jika terpotong
}) {
  const { unpinned, pinned } = useMemo(() => {
    if (typeof pinRowPredicate !== "function")
      return { unpinned: data || [], pinned: [] };
    const p = [],
      u = [];
    (data || []).forEach((row) =>
      pinRowPredicate(row) ? p.push(row) : u.push(row)
    );
    return { unpinned: u, pinned: p };
  }, [data, pinRowPredicate]);

  let bodyRows = unpinned;
  let overflow = 0;

  if (Number.isFinite(maxRows) && maxRows > 0) {
    // sisakan ruang jika ada pinned rows (biar tetap muat di bawah)
    const roomForUnpinned = Math.max(0, maxRows - pinned.length);
    if (unpinned.length > roomForUnpinned) {
      overflow = unpinned.length - roomForUnpinned;
      bodyRows = unpinned.slice(0, roomForUnpinned);
    }
  }

  const displayRows = [...bodyRows, ...pinned];

  return (
    <div className="relative overflow-x-auto border rounded-xl shadow-sm">
      <table className="min-w-full text-sm">
        <thead className="bg-white border-b sticky top-0">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 font-semibold text-gray-700 whitespace-nowrap ${
                  (col.align || (col.numeric ? "right" : "left")) === "right"
                    ? "text-right"
                    : "text-left"
                }`}
              >
                {col.label ?? col.key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-6 text-center text-gray-500"
              >
                No data
              </td>
            </tr>
          )}
          {displayRows.map((row, idx) => {
            const pinnedRow = pinRowPredicate?.(row);
            return (
              <tr
                key={idx}
                className={`${
                  striped ? (idx % 2 ? "bg-gray-50" : "bg-white") : "bg-white"
                } ${pinnedRow ? "font-semibold border-t-2" : ""}`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 py-2 whitespace-nowrap ${
                      (col.align || (col.numeric ? "right" : "left")) ===
                      "right"
                        ? "text-right"
                        : "text-left"
                    }`}
                  >
                    {typeof col.render === "function"
                      ? col.render(row?.[col.key], row)
                      : row?.[col.key] ?? "—"}
                  </td>
                ))}
              </tr>
            );
          })}
          {overflow > 0 && showOverflowHint && (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-2 text-center text-gray-500 italic"
              >
                … +{overflow} more rows
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
